/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { getCookie } from 'cookies-next';

/**
 * NGUỒN SỰ THẬT DUY NHẤT cho loại phiên (webinapp vs web).
 *
 * Trước đây loại phiên bị SUY LUẬN mỗi lần load từ `hasRefreshToken()` + cookie
 * `token` nhập nhằng → webinapp lâu lâu bị nhận nhầm thành web → chạy nhánh web
 * (đọc token sai + 401 → terminalLogout) → "báo đăng xuất oan". Giờ loại phiên là
 * một MARKER ghi dứt khoát lúc vào phiên, đọc ở 1 chỗ, mọi nhánh auth rẽ theo nó.
 *
 * Lưu 2 lớp:
 *  - cookie `session_type` (Domain=.ahafood.ai, 30 ngày): server/SSR + lần load đầu
 *    đọc được ngay; web set qua /auth/callback (server).
 *  - localStorage mirror: lớp bền phụ khi webview drop cookie.
 *
 * refresh_token KHÔNG còn vai trò phân loại (chính nó gây bug).
 */

export type SessionType = 'web' | 'webinapp';

export const SESSION_TYPE_COOKIE = 'session_type';
const LS_KEY = 'ahafood.session_type';
const MAX_AGE = 30 * 24 * 60 * 60; // 30 ngày

function isAhafoodDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'ahafood.ai' || host.endsWith('.ahafood.ai');
}

/** Đọc loại phiên: cookie → localStorage. null = ẩn danh / chưa xác định. */
export function getSessionType(): SessionType | null {
  const c = getCookie(SESSION_TYPE_COOKIE);
  if (c === 'web' || c === 'webinapp') return c;
  if (typeof window === 'undefined') return null;
  try {
    const ls = localStorage.getItem(LS_KEY);
    if (ls === 'web' || ls === 'webinapp') return ls;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Ghi marker client-side (cookie + localStorage). Web đã có cookie từ server
 * callback; gọi lại lúc hydrate để đồng bộ localStorage mirror. Webinapp gọi lúc
 * vào phiên (?token=).
 */
export function setSessionTypeClient(type: SessionType): void {
  if (typeof document === 'undefined') return;
  const isHttps = window.location.protocol === 'https:';
  const parts = [
    `${SESSION_TYPE_COOKIE}=${type}`,
    'path=/',
    'SameSite=Lax',
    `max-age=${MAX_AGE}`,
  ];
  if (isAhafoodDomain()) parts.push('domain=.ahafood.ai');
  if (isHttps) parts.push('Secure');
  document.cookie = parts.join('; ');
  try {
    localStorage.setItem(LS_KEY, type);
  } catch {
    /* ignore */
  }
}

/** Xoá marker (logout / entry reset). */
export function clearSessionType(): void {
  if (typeof document === 'undefined') return;
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `${SESSION_TYPE_COOKIE}=; path=/; ${expire}`;
  if (isAhafoodDomain()) {
    document.cookie = `${SESSION_TYPE_COOKIE}=; path=/; domain=.ahafood.ai; ${expire}`;
  }
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
}
